import { checkPerm } from 'utils/permManager'

import {
  selectActualBillableAgent,
  selectActualFeatureFlags,
  selectIsActualCompanyDataReady,
  selectPartner,
  selectSelfPermissions,
} from 'containers/App/selectors'
import { selectCompanyFilter, selectCompanyLoader } from 'containers/PartnerOptions/selectors'

import { PSIMA_NOT_ENABLED_ERROR_CODE, PSIMA_PAGE_STATUS } from './constants'
import {
  selectAccessError,
  selectPsimaCompaniesError,
  selectPsimaCompaniesLoaded,
  selectPsimaCompanyIds,
} from './selectors'

export const selectHasPsimaRole = (state) =>
  checkPerm(selectSelfPermissions(state), ['admin', 'edit_campaigns'])

const selectIsPsimaEnabledForActualCompany = (state) => {
  const isFeatureEnabled = selectActualFeatureFlags(state).PSIMA_SERVICE
  // Child billing is absent locally; selectActualBillableAgent never substitutes parent billing.
  const isBillingEnabled = selectActualBillableAgent(state)?.psimaAgent ?? true
  const isDisabledByBackend = selectAccessError(state)?.code === PSIMA_NOT_ENABLED_ERROR_CODE

  return Boolean(isFeatureEnabled && isBillingEnabled && !isDisabledByBackend)
}

const selectNeedsPsimaCompanySelection = (state) => {
  if (!selectPartner(state)) return false

  // A failed list is unknown, not empty: keep the selected tenant and its escape path usable.
  if (selectPsimaCompaniesError(state)) return false
  if (!selectPsimaCompaniesLoaded(state)) return true

  const selectedCompanyId = selectCompanyFilter(state)?.value
  const eligibleCompanyIds = selectPsimaCompanyIds(state)
  return !eligibleCompanyIds.includes(selectedCompanyId)
}

export const selectPsimaPageStatus = (state) => {
  if (!selectHasPsimaRole(state)) return PSIMA_PAGE_STATUS.REDIRECT

  const isSingleCompany = !selectPartner(state)
  if (isSingleCompany && !selectIsPsimaEnabledForActualCompany(state)) {
    return PSIMA_PAGE_STATUS.REDIRECT
  }

  // During a switch, selectActual* can still fall back to the partner's own data.
  if (selectCompanyLoader(state)) return PSIMA_PAGE_STATUS.LOADING

  if (selectNeedsPsimaCompanySelection(state)) {
    return selectPsimaCompaniesLoaded(state)
      ? PSIMA_PAGE_STATUS.CHOOSE_COMPANY
      : PSIMA_PAGE_STATUS.LOADING
  }

  // A missing company row will not retry itself, so a spinner here would never resolve.
  if (!selectIsActualCompanyDataReady(state)) return PSIMA_PAGE_STATUS.TENANT_UNAVAILABLE
  if (!selectIsPsimaEnabledForActualCompany(state)) return PSIMA_PAGE_STATUS.NOT_ENABLED
  if (selectAccessError(state)) return PSIMA_PAGE_STATUS.ACCESS_DENIED
  return PSIMA_PAGE_STATUS.READY
}

export const selectShowPsimaCompanySelector = (state) => {
  if (!selectPartner(state)) return false

  const hasChooserOptions = selectPsimaCompanyIds(state).length > 0
  if (selectNeedsPsimaCompanySelection(state) && hasChooserOptions) return false

  return true
}
